/**
 * A GALÉRIAKÉPEK CSOPORTOSÍTÁSA — melyik kép melyik számla oldala (#50).
 *
 * A mérce: `docs/tervezes/design/kliens-uj-kinezet/galeria-oldalak.html`, az
 * 1. út (Viktor, 2026-11-04). Az ügyfél a galériából több képet választ; ma
 * minden kép külön bizonylat lesz, és egy kétoldalas számla két félbizonylatra
 * esik szét. Itt a képek SORBAN állnak, és két SZOMSZÉD kép közé egy
 * „Összefűzöm" kerül: az összefűzött képek egy számla oldalai.
 *
 * ═══ AZ ÁLLAPOT: KÉPEK + ILLESZTÉSEK ═══
 *
 * Nem „csoportok listája", hanem a képek sorrendje és köztük egy-egy
 * igen/nem. Két okból:
 *
 *   1. Egy koppintás EGY illesztést fordít át — a csoportokat ebből számoljuk,
 *      nem tároljuk külön, tehát nem csúszhatnak szét a képek sorrendjétől.
 *   2. Az áthelyezés (↑/↓) a KÉPET mozgatja, az illesztés a HELYÉN marad. Így a
 *      rossz sorrendben kiválasztott oldal a helyére tehető anélkül, hogy a
 *      csoportok határa elmozdulna.
 *
 * Tiszta függvények: a komponens csak meghívja őket, a szabályok fájlok és
 * DOM nélkül tesztelhetők.
 */

export interface Grouping<T> {
  items: readonly T[];
  /**
   * `joins[i]` = az `i`. és az `i + 1`. kép EGY számla. Hossza mindig
   * `items.length - 1` (üres listánál 0).
   */
  joins: readonly boolean[];
}

/** Kiinduló állapot: `together` = mind egy számla, különben mind külön. */
export function startGrouping<T>(items: readonly T[], together: boolean): Grouping<T> {
  return { items, joins: items.slice(1).map(() => together) };
}

/** A csoportok, a képek sorrendjében. */
export function groupsOf<T>({ items, joins }: Grouping<T>): T[][] {
  const groups: T[][] = [];
  items.forEach((item, index) => {
    if (index > 0 && joins[index - 1]) {
      groups[groups.length - 1].push(item);
    } else {
      groups.push([item]);
    }
  });
  return groups;
}

/** Az `index`. és a következő kép közti illesztés átfordítása. */
export function toggleJoin<T>(grouping: Grouping<T>, index: number): Grouping<T> {
  if (index < 0 || index >= grouping.joins.length) {
    return grouping;
  }
  const joins = [...grouping.joins];
  joins[index] = !joins[index];
  return { items: grouping.items, joins };
}

/**
 * EGY CSOPORT SZÉTVÁLASZTÁSA — a csoport belső illesztései mind kinyílnak.
 * A `groupIndex` a `groupsOf` sorszáma.
 */
export function splitGroup<T>(grouping: Grouping<T>, groupIndex: number): Grouping<T> {
  const joins = [...grouping.joins];
  let group = 0;
  for (let index = 0; index < joins.length; index++) {
    if (!joins[index]) {
      group += 1;
      continue;
    }
    if (group === groupIndex) {
      joins[index] = false;
    }
  }
  return { items: grouping.items, joins };
}

/**
 * A KÉP ÁTHELYEZÉSE eggyel előrébb vagy hátrébb. Az illesztések a HELYÜKÖN
 * maradnak: a kép mozog, a számlák határa nem (lásd a fejlécet).
 */
export function moveItem<T>(grouping: Grouping<T>, index: number, delta: -1 | 1): Grouping<T> {
  const target = index + delta;
  if (
    index < 0 ||
    index >= grouping.items.length ||
    target < 0 ||
    target >= grouping.items.length
  ) {
    return grouping;
  }
  const items = [...grouping.items];
  [items[index], items[target]] = [items[target], items[index]];
  return { items, joins: grouping.joins };
}

/**
 * A KÉP ELTÁVOLÍTÁSA. A két szomszédja akkor marad egy számla, ha MINDKÉT
 * illesztés zárva volt — egy csoport közepéből törölve a csoport egyben
 * marad, két csoport határán törölve a határ megmarad.
 */
export function removeItem<T>(grouping: Grouping<T>, index: number): Grouping<T> {
  const { items, joins } = grouping;
  if (index < 0 || index >= items.length) {
    return grouping;
  }
  const nextItems = items.filter((_, at) => at !== index);
  if (nextItems.length < 2) {
    return { items: nextItems, joins: [] };
  }
  const nextJoins = [...joins];
  if (index === 0) {
    nextJoins.splice(0, 1);
  } else if (index === items.length - 1) {
    nextJoins.splice(index - 1, 1);
  } else {
    nextJoins.splice(index - 1, 2, joins[index - 1] && joins[index]);
  }
  return { items: nextItems, joins: nextJoins };
}

/**
 * KELL-E CSOPORTOSÍTANI? Csak akkor, ha legalább két kép jött, és MIND kép.
 *
 * A PDF kimarad: maga hordja az oldalait, és ma is jól megy (#50, 10-30-i
 * mérés). Vegyes kiválasztásnál (kép + PDF) a mai út fut — fájlonként egy
 * bizonylat —, mert egy PDF-et nem lehet oldalként egy képhez fűzni.
 */
export function needsGrouping(files: readonly File[]): boolean {
  return files.length >= 2 && files.every((file) => file.type.startsWith('image/'));
}
